import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { dataService } from '../services/dataService';
import { emailVerificationService } from '../services/emailVerificationService';
import '../styles/landing-redesign.css';

const LandingRedesign: React.FC = () => {
  const navigate = useNavigate();
  const { login, error } = useApp();
  
  // Intro animation state
  const [showIntro, setShowIntro] = useState(true);
  const [introPhase, setIntroPhase] = useState(0);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'coach' as 'coach' | 'headteacher' | 'headmaster' | 'admin',
    firstName: '',
    lastName: ''
  });
  const [showTeacherFields, setShowTeacherFields] = useState(false);
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    schoolId: '',
    role: 'coach' as 'coach' | 'headteacher'
  });
  
  // UI states
  const [showLogin, setShowLogin] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  
  // Email verification states
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [hasEnteredNames, setHasEnteredNames] = useState(false);
  const [showCoachVerification, setShowCoachVerification] = useState(false);
  
  // Password visibility and caps lock states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  // School search states
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState<{ id: string; name: string }[]>([]);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedSchoolName, setSelectedSchoolName] = useState('');

  // Scroll handler for nav
  useEffect(() => {
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intro animation sequence
  useEffect(() => {
    // Phase 1: Logo reveal (0-1000ms)
    const phase1 = setTimeout(() => setIntroPhase(1), 100);
    // Phase 2: Text reveal (1000-2000ms)
    const phase2 = setTimeout(() => setIntroPhase(2), 800);
    // Phase 3: Tagline (2000-2500ms)
    const phase3 = setTimeout(() => setIntroPhase(3), 1600);
    // Phase 4: Stats counter (2500-3500ms)
    const phase4 = setTimeout(() => setIntroPhase(4), 2200);
    // Phase 5: Exit animation (3500-4000ms)
    const phase5 = setTimeout(() => setIntroPhase(5), 3200);
    // Hide intro completely
    const hideIntro = setTimeout(() => {
      setShowIntro(false);
    }, 3800);

    return () => {
      clearTimeout(phase1);
      clearTimeout(phase2);
      clearTimeout(phase3);
      clearTimeout(phase4);
      clearTimeout(phase5);
      clearTimeout(hideIntro);
    };
  }, []);

  // Toast helper
  const showSuccessToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  // Handle Caps Lock detection
  const handleKeyPress = (e: React.KeyboardEvent) => {
    setCapsLockOn(e.getModifierState?.('CapsLock') ?? false);
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      showSuccessToast('❌ Email Required\n\nPlease enter your email address.', 'error');
      return;
    }

    try {
      setIsResettingPassword(true);
      const emailExists = await authService.checkEmailExists(forgotPasswordEmail);
      if (!emailExists) {
        showSuccessToast('❌ Email Not Found\n\nNo account found with this email address.', 'error');
        return;
      }

      await emailVerificationService.sendPasswordResetCode(forgotPasswordEmail);
      showSuccessToast('📧 Reset Code Sent!\n\nA password reset code has been sent to your email address.');
      setShowForgotPassword(false);
      setShowPasswordReset(true);
      startResendTimer();
    } catch (error) {
      console.error('Forgot password error:', error);
      showSuccessToast('❌ Error\n\nFailed to send reset code. Please try again.', 'error');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!resetCode || resetCode.length !== 6) {
      showSuccessToast('❌ Verification Required\n\nPlease enter the 6-digit verification code.', 'error');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      showSuccessToast('❌ Password Too Short\n\nPassword must be at least 6 characters long.', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showSuccessToast('❌ Passwords Don\'t Match\n\nPlease make sure both passwords match.', 'error');
      return;
    }

    try {
      setIsResettingPassword(true);
      const isValid = emailVerificationService.verifyPasswordResetCode(forgotPasswordEmail, resetCode);
      if (!isValid) {
        showSuccessToast('❌ Invalid Code\n\nThe verification code is incorrect or has expired.', 'error');
        return;
      }

      const result = await authService.resetPassword(forgotPasswordEmail, newPassword);
      if (result.success) {
        emailVerificationService.clearPasswordResetCode(forgotPasswordEmail);
        showSuccessToast('✅ Password Reset Successful!\n\nYour password has been updated.');
        setShowPasswordReset(false);
        setForgotPasswordEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showSuccessToast(`❌ Reset Failed\n\n${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      showSuccessToast('❌ Error\n\nFailed to reset password. Please try again.', 'error');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dbRole = formData.role === 'headteacher' ? 'headmaster' : formData.role;
      
      if (showTeacherFields && formData.role === 'headteacher' && hasEnteredNames) {
        if (!verificationCode || verificationCode.length !== 6) {
          showSuccessToast('❌ Verification Required\n\nPlease enter the 6-digit verification code.', 'error');
          return;
        }
        const isValid = emailVerificationService.verifyCode(verificationEmail, verificationCode);
        if (!isValid) {
          showSuccessToast('❌ Invalid Code\n\nThe verification code is incorrect or has expired.', 'error');
          return;
        }
        showSuccessToast('✅ Email Verified!\n\nProceeding with registration...');
      }
      
      const result = await login(formData.email, formData.password, dbRole, formData.firstName, formData.lastName);
      
      if (result.needsRegistration) {
        setShowTeacherFields(true);
        return;
      }
      
      navigate('/dashboard');
    } catch (error) {
      // Error handled in context
    }
  };

  // Handle headmaster email verification
  const handleHeadmasterVerification = async (email?: string, fullName?: string) => {
    try {
      setIsVerifying(true);
      const targetEmail = email || formData.email;
      const targetName = fullName || `${formData.firstName} ${formData.lastName}`.trim() || undefined;
      setVerificationEmail(targetEmail);
      
      await emailVerificationService.sendVerificationCode(targetEmail, targetName);
      showSuccessToast('📧 Verification Code Sent!\n\nCheck your inbox for the code.');
      setShowVerification(true);
      startResendTimer();
    } catch (error) {
      console.error('Verification email error:', error);
      showSuccessToast('❌ Error\n\nFailed to send verification email.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle closing registration modal
  const handleCloseRegister = () => {
    setShowRegister(false);
    setShowCoachVerification(false);
    setShowVerification(false);
    setVerificationCode('');
    setVerificationEmail('');
    setRegisterData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      schoolId: '',
      role: 'coach'
    });
    setSchoolSearch('');
    setSelectedSchoolName('');
    setSchoolSuggestions([]);
    setShowSchoolDropdown(false);
  };

  // Handle coach email verification
  const handleCoachVerification = async () => {
    try {
      setIsVerifying(true);
      const targetEmail = registerData.email;
      const targetName = `${registerData.firstName} ${registerData.lastName}`.trim();
      setVerificationEmail(targetEmail);
      
      await emailVerificationService.sendVerificationCode(targetEmail, targetName);
      showSuccessToast('📧 Verification Code Sent!\n\nCheck your inbox for the code.');
      setShowCoachVerification(true);
      setShowVerification(true);
      startResendTimer();
    } catch (error) {
      console.error('Verification email error:', error);
      showSuccessToast('❌ Error\n\nFailed to send verification email.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  // Start resend timer
  const startResendTimer = () => {
    setCanResend(false);
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Helper function to detect headmaster email
  const isHeadmasterEmail = (email: string): boolean => {
    const emailPattern = /^hm\.[a-zA-Z0-9]+@moe\.edu\.gy$/i;
    return emailPattern.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let newFormData = { ...formData, [name]: value };
    
    if (name === 'email' && isHeadmasterEmail(value)) {
      newFormData.role = 'headteacher';
    }
    
    setFormData(newFormData);
    
    if (showTeacherFields && formData.role === 'headteacher' && !hasEnteredNames) {
      if ((name === 'firstName' && value.trim() && newFormData.lastName.trim()) ||
          (name === 'lastName' && value.trim() && newFormData.firstName.trim())) {
        setHasEnteredNames(true);
        handleHeadmasterVerification(newFormData.email, `${newFormData.firstName} ${newFormData.lastName}`);
      }
    }
    
    if (name === 'role' || name === 'email') {
      setShowTeacherFields(false);
      setHasEnteredNames(false);
      setShowVerification(false);
    }
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
  };

  // School search functionality
  const handleSchoolSearch = async (value: string) => {
    setSchoolSearch(value);
    setShowSchoolDropdown(true);
    
    if (value.length >= 2) {
      try {
        const suggestions = await dataService.searchSchools(value);
        setSchoolSuggestions(suggestions);
      } catch (error) {
        console.error('Error searching schools:', error);
        setSchoolSuggestions([]);
      }
    } else {
      setSchoolSuggestions([]);
    }
  };

  const handleSchoolSelect = (school: { id: string; name: string }) => {
    setRegisterData({ ...registerData, schoolId: school.id });
    setSelectedSchoolName(school.name);
    setSchoolSearch(school.name);
    setShowSchoolDropdown(false);
    setSchoolSuggestions([]);
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (showCoachVerification) {
        if (!verificationCode || verificationCode.length !== 6) {
          showSuccessToast('❌ Verification Required\n\nPlease enter the 6-digit verification code.', 'error');
          return;
        }
        
        const isValid = emailVerificationService.verifyCode(verificationEmail, verificationCode);
        if (!isValid) {
          showSuccessToast('❌ Invalid Code\n\nThe verification code is incorrect or has expired.', 'error');
          return;
        }
        
        showSuccessToast('✅ Email Verified!\n\nProceeding with registration...');
        
        const registrationData = { ...registerData, role: 'coach' as const };
        await authService.register(registrationData);
        showSuccessToast('🎉 Registration Successful!\n\nYou can now login with your credentials.');
        
        handleCloseRegister();
        setShowLogin(true);
        return;
      }
      
      // Validate fields before sending verification
      if (!registerData.firstName || !registerData.lastName || !registerData.email || !registerData.password || !registerData.schoolId) {
        showSuccessToast('❌ Missing Fields\n\nPlease fill in all required fields.', 'error');
        return;
      }
      
      await handleCoachVerification();
    } catch (error) {
      console.error('Registration error:', error);
      showSuccessToast('❌ Registration Failed\n\nPlease try again.', 'error');
    }
  };

  // Eye icon SVG component
  const EyeIcon = ({ visible }: { visible: boolean }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      {visible ? (
        <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z"/>
      ) : (
        <path d="M11.83,9L15,12.16C15,12.11 15,12.05 15,12A3,3 0 0,0 12,9C11.94,9 11.89,9 11.83,9M7.53,9.8L9.08,11.35C9.03,11.56 9,11.77 9,12A3,3 0 0,0 12,15C12.22,15 12.44,14.97 12.65,14.92L14.2,16.47C13.53,16.8 12.79,17 12,17A5,5 0 0,1 7,12C7,11.21 7.2,10.47 7.53,9.8M2,4.27L4.28,6.55L4.73,7C3.08,8.3 1.78,10 1,12C2.73,16.39 7,19.5 12,19.5C13.55,19.5 15.03,19.2 16.38,18.66L16.81,19.09L19.73,22L21,20.73L3.27,3M12,7A5,5 0 0,1 17,12C17,12.64 16.87,13.26 16.64,13.82L19.57,16.75C21.07,15.5 22.27,13.86 23,12C21.27,7.61 17,4.5 12,4.5C10.6,4.5 9.26,4.75 8,5.2L10.17,7.35C10.76,7.13 11.37,7 12,7Z"/>
      )}
    </svg>
  );

  return (
    <div className="landing-redesign">
      {/* ========== INTRO SPLASH SCREEN - Sprint Start Experience ========== */}
      {showIntro && (
        <div className={`intro-overlay ${introPhase >= 5 ? 'intro-exit' : ''}`}>
          {/* Stadium lights flare effect */}
          <div className="stadium-lights">
            <div className={`stadium-light light-1 ${introPhase >= 1 ? 'light-on' : ''}`} />
            <div className={`stadium-light light-2 ${introPhase >= 1 ? 'light-on' : ''}`} />
            <div className={`stadium-light light-3 ${introPhase >= 1 ? 'light-on' : ''}`} />
          </div>

          {/* Sprint command sequence */}
          <div className="sprint-commands">
            <div className={`command-text command-marks ${introPhase >= 1 ? 'command-visible' : ''} ${introPhase >= 2 ? 'command-fade' : ''}`}>
              ON YOUR MARKS
            </div>
            <div className={`command-text command-set ${introPhase >= 2 ? 'command-visible' : ''} ${introPhase >= 3 ? 'command-fade' : ''}`}>
              SET
            </div>
            <div className={`command-text command-go ${introPhase >= 3 ? 'command-visible' : ''}`}>
              <span className="go-text">GO!</span>
              <div className="go-explosion">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="explosion-particle" style={{ transform: `rotate(${i * 30}deg)` }} />
                ))}
              </div>
            </div>
          </div>

          {/* Main branding reveal */}
          <div className={`intro-branding ${introPhase >= 4 ? 'branding-visible' : ''}`}>
            <img 
              src="/assets/moe-logo.jpg" 
              alt="MOE" 
              className="intro-brand-logo"
            />
            <div className="intro-brand-text">
              <span className="brand-line brand-line-1">REGIONAL INTER SECONDARY</span>
              <span className="brand-line brand-line-2">SCHOOLS ATHLETICS</span>
              <span className="brand-tagline">Where Champions Are Born</span>
            </div>
          </div>

          {/* Pulse rings on GO */}
          <div className={`pulse-rings ${introPhase >= 3 ? 'rings-active' : ''}`}>
            <div className="pulse-ring ring-1" />
            <div className="pulse-ring ring-2" />
            <div className="pulse-ring ring-3" />
          </div>

          {/* Speed lines */}
          <div className={`speed-lines ${introPhase >= 4 ? 'speed-active' : ''}`}>
            {[...Array(20)].map((_, i) => (
              <div 
                key={i} 
                className="speed-line"
                style={{ 
                  top: `${5 + (i * 4.5)}%`,
                  animationDelay: `${Math.random() * 0.3}s`,
                  width: `${30 + Math.random() * 40}%`
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="track-pattern-bg" />
      <div className="particle-field">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="particle" />
        ))}
      </div>

      {/* Navigation */}
      <nav className={`athletic-nav ${isNavScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-brand">
            <img 
              src="/assets/moe-logo.jpg" 
              alt="MOE Athletics" 
              className="nav-logo-img"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="45" fill="%23C41E3A"/%3E%3Ctext x="50" y="60" text-anchor="middle" fill="white" font-size="24" font-weight="bold"%3EMOE%3C/text%3E%3C/svg%3E';
              }}
            />
            <div className="nav-brand-text">
              <h1 className="nav-title">Athletics</h1>
              <span className="nav-subtitle">Regional Inter Secondary Schools</span>
            </div>
          </Link>

          <div className="nav-menu">
            <Link to="/" className="nav-menu-link active">Home</Link>
            <Link to="/track-events" className="nav-menu-link">Track Events</Link>
            <Link to="/field-events" className="nav-menu-link">Field Events</Link>
          </div>

          <div className="nav-actions">
            <button 
              className="btn-athletic btn-secondary-athletic"
              onClick={() => { setShowLogin(true); setShowRegister(false); }}
            >
              Login
            </button>
            <button 
              className="btn-athletic btn-primary-athletic"
              onClick={() => { setShowRegister(true); setShowLogin(false); }}
            >
              Register
            </button>
          </div>

          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-athletic">
        <div 
          className="hero-bg-image" 
          style={{ backgroundImage: 'url(/assets/landingwallpaper.jpg)' }} 
        />
        
        {/* Animated Diagonal Lines */}
        <div className="hero-diagonal-lines">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="diagonal-line" />
          ))}
        </div>
        
        <div className="hero-overlay-gradient" />

        <div className="hero-content">
          {/* Text Section */}
          <div className="hero-text-section">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              <span className="hero-badge-text">Registration Now Open</span>
            </div>

            <h1 className="hero-title">
              <span className="hero-title-line">
                <span>Regional</span>
              </span>
              <span className="hero-title-line">
                <span className="hero-title-highlight">Inter Secondary</span>
              </span>
              <span className="hero-title-line">
                <span>Schools Athletics</span>
              </span>
            </h1>

            <p className="hero-tagline">Where Champions Are Born</p>

            <div className="hero-cta-group">
              <button 
                className="btn-athletic btn-primary-athletic"
                onClick={() => { setShowLogin(true); setShowRegister(false); }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                </svg>
                Login Now
              </button>
              <button 
                className="btn-athletic btn-track-athletic"
                onClick={() => { setShowRegister(true); setShowLogin(false); }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z" />
                </svg>
                Register School
              </button>
            </div>
          </div>

          {/* Auth Card Section */}
          <div className="auth-card-section">
            {showLogin && !showRegister && (
              <div className="auth-card">
                <div className="auth-card-header">
                  <h2 className="auth-card-title">Welcome Back</h2>
                  <p className="auth-card-subtitle">Sign in to access your dashboard</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                  {error && (
                    <div className="alert-athletic alert-error">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z" />
                      </svg>
                      {error}
                    </div>
                  )}

                  <div className="form-group-athletic">
                    <label className="form-label-athletic">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="form-select-athletic"
                      required
                    >
                      <option value="coach">Coach</option>
                      <option value="headteacher">Headteacher</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="form-group-athletic">
                    <label className="form-label-athletic">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input-athletic"
                      placeholder="Enter your email"
                      required
                    />
                    {isHeadmasterEmail(formData.email) && (
                      <small style={{ color: 'var(--electric-blue)', marginTop: '0.25rem', display: 'block' }}>
                        Headmaster email detected - role set automatically
                      </small>
                    )}
                  </div>

                  <div className="form-group-athletic">
                    <label className="form-label-athletic">Password</label>
                    <div className="form-password-wrapper">
                      <input
                        type={showLoginPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        onKeyDown={handleKeyPress}
                        className="form-input-athletic"
                        placeholder="Enter your password"
                        style={{ paddingRight: '3rem' }}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="form-password-toggle"
                      >
                        <EyeIcon visible={showLoginPassword} />
                      </button>
                    </div>
                    {capsLockOn && (
                      <div className="caps-lock-warning">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2Z"/>
                        </svg>
                        Caps Lock is ON
                      </div>
                    )}
                  </div>

                  {showTeacherFields && (
                    <>
                      <div className="alert-athletic alert-info">
                        Please provide your details to complete registration.
                      </div>
                      
                      <div className="name-row-athletic">
                        <div className="form-group-athletic">
                          <label className="form-label-athletic">First Name</label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="form-input-athletic"
                            placeholder="First name"
                            required
                          />
                        </div>
                        <div className="form-group-athletic">
                          <label className="form-label-athletic">Last Name</label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="form-input-athletic"
                            placeholder="Last name"
                            required
                          />
                        </div>
                      </div>

                      {hasEnteredNames && formData.role === 'headteacher' && (
                        <div className="form-group-athletic">
                          <label className="form-label-athletic">🔐 Verification Code</label>
                          <input
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="form-input-athletic verification-input-athletic"
                            placeholder="000000"
                            maxLength={6}
                            required
                            disabled={isVerifying}
                          />
                          <div className="resend-code-row">
                            <span>Code sent to: {verificationEmail}</span>
                            <button
                              type="button"
                              onClick={() => handleHeadmasterVerification()}
                              disabled={!canResend || isVerifying}
                              className="resend-code-btn"
                            >
                              {canResend ? 'Resend Code' : `Resend in ${resendTimer}s`}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="form-actions-athletic">
                    <button
                      type="submit"
                      className="btn-athletic btn-primary-athletic"
                      disabled={formData.role === 'headteacher' && hasEnteredNames && verificationCode.length !== 6}
                      style={{ width: '100%' }}
                    >
                      {showTeacherFields 
                        ? (formData.role === 'headteacher' && hasEnteredNames 
                          ? 'Verify & Complete' 
                          : 'Complete Registration')
                        : 'Sign In'}
                    </button>

                    <div className="form-link-athletic">
                      <button type="button" onClick={() => setShowForgotPassword(true)}>
                        Forgot your password?
                      </button>
                    </div>

                    <div className="form-link-athletic">
                      Don't have an account?{' '}
                      <button 
                        type="button" 
                        onClick={() => { setShowRegister(true); setShowLogin(false); }}
                      >
                        Register here
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {showRegister && !showLogin && (
              <div className="auth-card register-card-athletic">
                <div className="auth-card-header">
                  <h2 className="auth-card-title">Coach Registration</h2>
                  <p className="auth-card-subtitle">Create your account to register athletes</p>
                </div>

                <form onSubmit={handleRegisterSubmit} className="auth-form">
                  <div className="name-row-athletic">
                    <div className="form-group-athletic">
                      <label className="form-label-athletic">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={registerData.firstName}
                        onChange={handleRegisterChange}
                        className="form-input-athletic"
                        placeholder="First name"
                        required
                        disabled={showCoachVerification}
                      />
                    </div>
                    <div className="form-group-athletic">
                      <label className="form-label-athletic">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={registerData.lastName}
                        onChange={handleRegisterChange}
                        className="form-input-athletic"
                        placeholder="Last name"
                        required
                        disabled={showCoachVerification}
                      />
                    </div>
                  </div>

                  <div className="form-group-athletic">
                    <label className="form-label-athletic">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={registerData.email}
                      onChange={handleRegisterChange}
                      className="form-input-athletic"
                      placeholder="Enter your email"
                      required
                      disabled={showCoachVerification}
                    />
                  </div>

                  <div className="form-group-athletic">
                    <label className="form-label-athletic">Password</label>
                    <div className="form-password-wrapper">
                      <input
                        type={showRegisterPassword ? 'text' : 'password'}
                        name="password"
                        value={registerData.password}
                        onChange={handleRegisterChange}
                        onKeyDown={handleKeyPress}
                        className="form-input-athletic"
                        placeholder="Create a password"
                        style={{ paddingRight: '3rem' }}
                        required
                        disabled={showCoachVerification}
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="form-password-toggle"
                      >
                        <EyeIcon visible={showRegisterPassword} />
                      </button>
                    </div>
                    {capsLockOn && (
                      <div className="caps-lock-warning">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2Z"/>
                        </svg>
                        Caps Lock is ON
                      </div>
                    )}
                  </div>

                  <div className="form-group-athletic school-search-wrapper">
                    <label className="form-label-athletic">School</label>
                    <input
                      type="text"
                      value={schoolSearch}
                      onChange={(e) => handleSchoolSearch(e.target.value)}
                      className="form-input-athletic"
                      placeholder="Search for your school..."
                      required
                      disabled={showCoachVerification}
                    />
                    {showSchoolDropdown && schoolSuggestions.length > 0 && (
                      <div className="school-dropdown">
                        {schoolSuggestions.map((school) => (
                          <div
                            key={school.id}
                            className="school-dropdown-item"
                            onClick={() => handleSchoolSelect(school)}
                          >
                            {school.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {showCoachVerification && (
                    <div className="form-group-athletic">
                      <label className="form-label-athletic">🔐 Verification Code</label>
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="form-input-athletic verification-input-athletic"
                        placeholder="000000"
                        maxLength={6}
                        required
                        disabled={isVerifying}
                      />
                      <div className="resend-code-row">
                        <span>Code sent to: {verificationEmail}</span>
                        <button
                          type="button"
                          onClick={() => handleCoachVerification()}
                          disabled={!canResend || isVerifying}
                          className="resend-code-btn"
                        >
                          {isVerifying ? 'Sending...' : canResend ? 'Resend Code' : `Resend in ${resendTimer}s`}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="form-actions-athletic">
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        type="submit"
                        className="btn-athletic btn-primary-athletic"
                        disabled={isVerifying}
                        style={{ flex: 1 }}
                      >
                        {showCoachVerification ? 'Verify & Register' : isVerifying ? 'Sending...' : 'Send Verification Code'}
                      </button>
                      {showCoachVerification && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowCoachVerification(false);
                            setShowVerification(false);
                            setVerificationCode('');
                            setVerificationEmail('');
                          }}
                          className="btn-athletic btn-secondary-athletic"
                        >
                          Back
                        </button>
                      )}
                    </div>

                    <div className="form-link-athletic">
                      Already have an account?{' '}
                      <button 
                        type="button" 
                        onClick={() => { setShowLogin(true); handleCloseRegister(); }}
                      >
                        Sign in
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Stats Section - positioned after auth card on mobile */}
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-number">50+</div>
              <div className="hero-stat-label">Schools</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-number">1000+</div>
              <div className="hero-stat-label">Athletes</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-number">30+</div>
              <div className="hero-stat-label">Events</div>
            </div>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {showToast && (
        <div className={`toast-athletic ${toastType}`}>
          <div className="toast-athletic-content">
            <span className="toast-athletic-icon">
              {toastType === 'success' ? '✅' : '⚠️'}
            </span>
            <div className="toast-athletic-message">{toastMessage}</div>
            <button 
              className="toast-athletic-close"
              onClick={() => setShowToast(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-athletic-overlay">
          <div className="modal-athletic">
            <div className="modal-athletic-header">
              <h3 className="modal-athletic-title">Forgot Password</h3>
              <p className="modal-athletic-subtitle">
                Enter your email address and we'll send you a verification code.
              </p>
            </div>

            <div className="auth-form">
              <div className="form-group-athletic">
                <label className="form-label-athletic">Email Address</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="form-input-athletic"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="modal-athletic-actions">
                <button
                  onClick={handleForgotPassword}
                  disabled={isResettingPassword}
                  className="btn-athletic btn-primary-athletic"
                >
                  {isResettingPassword ? 'Sending...' : 'Send Reset Code'}
                </button>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                  }}
                  className="btn-athletic btn-secondary-athletic"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="modal-athletic-overlay">
          <div className="modal-athletic">
            <div className="modal-athletic-header">
              <h3 className="modal-athletic-title">Reset Password</h3>
              <p className="modal-athletic-subtitle">
                Enter the verification code sent to <strong>{forgotPasswordEmail}</strong>
              </p>
            </div>

            <div className="auth-form">
              <div className="form-group-athletic">
                <label className="form-label-athletic">Verification Code</label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="form-input-athletic verification-input-athletic"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
                <div className="resend-code-row">
                  <span>Code sent to: {forgotPasswordEmail}</span>
                  <button
                    type="button"
                    onClick={() => emailVerificationService.sendPasswordResetCode(forgotPasswordEmail)}
                    disabled={!canResend || isResettingPassword}
                    className="resend-code-btn"
                  >
                    {isResettingPassword ? 'Sending...' : canResend ? 'Resend Code' : `Resend in ${resendTimer}s`}
                  </button>
                </div>
              </div>

              <div className="form-group-athletic">
                <label className="form-label-athletic">New Password</label>
                <div className="form-password-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="form-input-athletic"
                    placeholder="Enter new password"
                    style={{ paddingRight: '3rem' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="form-password-toggle"
                  >
                    <EyeIcon visible={showNewPassword} />
                  </button>
                </div>
              </div>

              <div className="form-group-athletic">
                <label className="form-label-athletic">Confirm Password</label>
                <div className="form-password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="form-input-athletic"
                    placeholder="Confirm new password"
                    style={{ paddingRight: '3rem' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="form-password-toggle"
                  >
                    <EyeIcon visible={showConfirmPassword} />
                  </button>
                </div>
                {capsLockOn && (
                  <div className="caps-lock-warning">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2Z"/>
                    </svg>
                    Caps Lock is ON
                  </div>
                )}
              </div>

              <div className="modal-athletic-actions">
                <button
                  onClick={handlePasswordReset}
                  disabled={isResettingPassword}
                  className="btn-athletic btn-primary-athletic"
                >
                  {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordReset(false);
                    setResetCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="btn-athletic btn-secondary-athletic"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingRedesign;
